import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { lotteryPurchaseApi, LotteryPurchaseEntry } from '../services/lotteryPurchaseService.ts';

const emptyForm = { date: '', invoiceNumber: '', bookId: '', ticketValue: '', ticketsInBook: '', totalBookAmount: '' };
type LotteryPurchaseForm = typeof emptyForm;

const LotteryPurchasePage: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const isAdmin = user?.type === 'admin';
  const { showSuccess, showError } = useSnackbar();

  const [entries, setEntries] = useState<LotteryPurchaseEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<LotteryPurchaseEntry | null>(null);
  const [form, setForm] = useState<LotteryPurchaseForm>(emptyForm);

  // Auto-saves ~900ms after the last change to any field, while correcting an
  // existing entry (editingEntry set). Adding a new entry still uses the Add button.
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveError, setAutoSaveError] = useState('');
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, []);

  const headerCellSx = {
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.82rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
    border: '2px solid #7f0000',
    py: 1.5,
    px: 2,
  };

  const fetchEntries = async () => {
    if (!sessionToken) return;
    try {
      setLoading(true);
      const response = await lotteryPurchaseApi.getEntries({ page: 1, limit: 100 }, sessionToken);
      setEntries(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch lottery purchases';
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken]);

  const openAdd = () => {
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    setEditingEntry(null);
    setAutoSaveStatus('idle');
    setAutoSaveError('');
    setForm({ ...emptyForm, date: new Date().toISOString().slice(0, 10) });
    setDialogOpen(true);
  };

  const openEdit = (entry: LotteryPurchaseEntry) => {
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    setEditingEntry(entry);
    setAutoSaveStatus('idle');
    setAutoSaveError('');
    setForm({
      date: entry.date.slice(0, 10),
      invoiceNumber: entry.invoiceNumber,
      bookId: entry.bookId,
      ticketValue: entry.ticketValue ? entry.ticketValue.toString() : '',
      ticketsInBook: entry.ticketsInBook ? entry.ticketsInBook.toString() : '',
      totalBookAmount: entry.totalBookAmount.toString(),
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!sessionToken || !isAdmin) return;
    if (!window.confirm('Delete this entry?')) return;
    try {
      await lotteryPurchaseApi.deleteEntry(id, sessionToken);
      setEntries(prev => prev.filter(e => e.id !== id));
      showSuccess('Entry deleted.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete entry';
      showError(message);
    }
  };

  // Validates a form snapshot and returns the parsed values, or null (with a
  // toast/status message) if invalid. Shared by the explicit Add flow and auto-save.
  const validateForm = (snapshot: LotteryPurchaseForm, onInvalid: (message: string) => void) => {
    if (!snapshot.date || !snapshot.invoiceNumber.trim() || !snapshot.bookId.trim() || !snapshot.totalBookAmount) {
      onInvalid('Please fill in Date, Invoice Number, Book ID, and Total Book Amount.');
      return null;
    }
    // Lottery Ticket Value and No. of Tickets in Book are optional: when both are
    // filled in, Total Book Amount is auto-calculated from them; otherwise Total
    // Book Amount is whatever was entered manually.
    const ticketValue = snapshot.ticketValue ? parseFloat(snapshot.ticketValue) : 0;
    if (snapshot.ticketValue && (isNaN(ticketValue) || ticketValue < 0)) {
      onInvalid('Lottery Ticket Value must be a valid number.');
      return null;
    }
    const ticketsInBook = snapshot.ticketsInBook ? parseInt(snapshot.ticketsInBook, 10) : 0;
    if (snapshot.ticketsInBook && (isNaN(ticketsInBook) || ticketsInBook < 1)) {
      onInvalid('No. of Tickets in Book must be a valid whole number.');
      return null;
    }
    const totalBookAmount = parseFloat(snapshot.totalBookAmount);
    if (isNaN(totalBookAmount) || totalBookAmount < 0) {
      onInvalid('Total Book Amount must be a valid number.');
      return null;
    }
    return {
      date: snapshot.date,
      invoiceNumber: snapshot.invoiceNumber.trim(),
      bookId: snapshot.bookId.trim(),
      ticketValue,
      ticketsInBook,
      totalBookAmount,
    };
  };

  const performAutoSave = useCallback(
    async (id: number, snapshot: LotteryPurchaseForm) => {
      if (!sessionToken) return;
      const parsed = validateForm(snapshot, (message) => {
        setAutoSaveStatus('error');
        setAutoSaveError(message);
      });
      if (!parsed) return;

      setAutoSaveStatus('saving');
      try {
        const updated = await lotteryPurchaseApi.updateEntry(id, parsed, sessionToken);
        setEntries(prev => prev.map(e => (e.id === id ? updated : e)));
        setAutoSaveStatus('saved');
      } catch (err) {
        setAutoSaveStatus('error');
        setAutoSaveError(err instanceof Error ? err.message : 'Failed to save changes');
      }
    },
    [sessionToken]
  );

  const handleFormChange = (field: keyof LotteryPurchaseForm, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if ((field === 'ticketValue' || field === 'ticketsInBook') && next.ticketValue !== '' && next.ticketsInBook !== '') {
        next.totalBookAmount = ((parseFloat(next.ticketValue) || 0) * (parseInt(next.ticketsInBook, 10) || 0)).toFixed(2);
      }
      if (editingEntry) {
        if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
        setAutoSaveStatus('pending');
        autoSaveTimeoutRef.current = setTimeout(() => {
          autoSaveTimeoutRef.current = null;
          performAutoSave(editingEntry.id, next);
        }, 900);
      }
      return next;
    });
  };

  const handleCloseDialog = async () => {
    if (autoSaveTimeoutRef.current && editingEntry) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
      await performAutoSave(editingEntry.id, form);
    }
    setDialogOpen(false);
    setEditingEntry(null);
    setAutoSaveStatus('idle');
  };

  const handleAdd = async () => {
    if (!sessionToken) return;
    const parsed = validateForm(form, showError);
    if (!parsed) return;

    setSaving(true);
    try {
      const created = await lotteryPurchaseApi.createEntry(parsed, sessionToken);
      setEntries(prev => [created, ...prev]);
      showSuccess('Entry added.');
      setDialogOpen(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add entry';
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d: string) =>
    d ? new Date(d.slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  const totalAmount = entries.reduce((sum, e) => sum + (Number(e.totalBookAmount) || 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ color: "#7f0000", fontWeight: 700, flex: "0 0 auto", whiteSpace: "nowrap" }}>
          Date: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Lottery Purchase
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}
          sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}>
          Add Entry
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {entries.length} entr{entries.length !== 1 ? 'ies' : 'y'}
        {entries.length > 0 && ` · Total: $${totalAmount.toFixed(2)}`}
      </Typography>

      <Paper sx={{ width: '100%', overflow: 'hidden', border: '2px solid #9e9e9e' }}>
        <TableContainer>
          <Table sx={{ borderCollapse: 'collapse' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                {['ID', 'Date', 'Invoice Number', 'Book ID', 'Lottery Ticket Value', 'No. of Tickets in Book', 'Total Book Amount', 'Actions'].map((h, i) => (
                  <TableCell
                    key={h}
                    align={i >= 4 ? 'right' : 'left'}
                    sx={{ ...headerCellSx, ...(h === 'Actions' ? { textAlign: 'center' } : {}) }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5, border: '2px solid #9e9e9e' }}>
                    <CircularProgress size={28} />
                    <Typography variant="body2" sx={{ mt: 1 }}>Loading lottery purchases...</Typography>
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5, border: '2px solid #9e9e9e', color: 'text.secondary' }}>
                    No entries yet. Click "Add Entry" to get started.
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry, idx) => (
                  <TableRow
                    key={entry.id}
                    sx={{
                      backgroundColor: idx % 2 === 0 ? '#fff' : '#fdf5f5',
                      '&:hover': { backgroundColor: '#fce4e4' },
                      '& td': { border: '2px solid #9e9e9e', py: 1.25, px: 2 },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{entry.id}</Typography>
                    </TableCell>
                    <TableCell>{formatDate(entry.date)}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{entry.invoiceNumber}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{entry.bookId}</TableCell>
                    <TableCell align="right">${(Number(entry.ticketValue) || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">{entry.ticketsInBook || 0}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="600">
                        ${(Number(entry.totalBookAmount) || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openEdit(entry)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isAdmin && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(entry.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
              {entries.length > 0 && (
                <TableRow sx={{ backgroundColor: '#b71c1c', '& td': { border: '2px solid #7f0000', py: 1.25, px: 2, color: '#fff', fontWeight: 700 } }}>
                  <TableCell colSpan={6}>TOTAL</TableCell>
                  <TableCell align="right">${totalAmount.toFixed(2)}</TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingEntry ? 'Edit Entry' : 'Add Lottery Purchase'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.date}
            onChange={e => handleFormChange('date', e.target.value)}
          />
          <TextField
            label="Invoice Number"
            fullWidth
            value={form.invoiceNumber}
            onChange={e => handleFormChange('invoiceNumber', e.target.value)}
          />
          <TextField
            label="Book ID"
            fullWidth
            value={form.bookId}
            onChange={e => handleFormChange('bookId', e.target.value)}
          />
          <TextField
            label="Lottery Ticket Value (optional)"
            type="number"
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
            value={form.ticketValue}
            onChange={e => handleFormChange('ticketValue', e.target.value)}
          />
          <TextField
            label="No. of Tickets in Book (optional)"
            type="number"
            fullWidth
            inputProps={{ min: 1, step: 1 }}
            value={form.ticketsInBook}
            onChange={e => handleFormChange('ticketsInBook', e.target.value)}
          />
          <TextField
            label="Total Book Amount"
            type="number"
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
            value={form.totalBookAmount}
            disabled={form.ticketValue !== '' && form.ticketsInBook !== ''}
            helperText={form.ticketValue !== '' && form.ticketsInBook !== ''
              ? 'Auto-calculated: Lottery Ticket Value × No. of Tickets in Book'
              : 'Enter manually, or fill in Ticket Value and Tickets in Book to auto-calculate'}
            onChange={e => handleFormChange('totalBookAmount', e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: editingEntry ? 'space-between' : 'flex-end' }}>
          {editingEntry ? (
            <>
              <Typography
                variant="body2"
                color={autoSaveStatus === 'error' ? 'error' : 'text.secondary'}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                {autoSaveStatus === 'saving' && (
                  <>
                    <CircularProgress size={14} />
                    Saving…
                  </>
                )}
                {autoSaveStatus === 'pending' && 'Unsaved changes…'}
                {autoSaveStatus === 'saved' && 'All changes saved'}
                {autoSaveStatus === 'error' && (autoSaveError || 'Failed to save changes')}
              </Typography>
              <Button onClick={handleCloseDialog} variant="contained" startIcon={<CloseIcon fontSize="small" />}
                disabled={autoSaveStatus === 'saving'}
                sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleAdd} disabled={saving}
                sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}>
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Add'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LotteryPurchasePage;
